import { FetchErrorMessage } from '@modules/common/components/FetchErrorMessage';
import { DashboardAnalysisHeader } from '@modules/dashboard/components/DashboardAnalysisHeader';
import { WarningAmberOutlined } from '@mui/icons-material';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { AnalysisUseCase, AnalysisViewFragment } from 'generated/graphql';
import { useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { DashboardAnalysisContent } from '../DashboardAnalysisContent';
import { DashboardAnalysisProvider } from './DashboardAnalysisProvider';

interface DashboardAnalysisProps {
  rowID: string;
  analysis: AnalysisViewFragment;
  shouldDisableInteraction?: boolean;
  onHeaderMouseEnter?: () => void;
  onHeaderMouseLeave?: () => void;
}

export const DashboardAnalysis = ({
  rowID,
  analysis,
  onHeaderMouseEnter,
  onHeaderMouseLeave,
  shouldDisableInteraction,
}: DashboardAnalysisProps) => {
  const updatedAnalysis = useMemo(() => {
    if (analysis.useCase === AnalysisUseCase.Compare) {
      return {
        ...analysis,
        useCase: AnalysisUseCase.Default,
      };
    }
    return analysis;
  }, [analysis]);

  return (
    <DashboardAnalysisProvider useCase={analysis.useCase}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: '6px',
          minWidth: '100%',
          height: '100%',
        }}
        data-testid={`dashboard-analysis-card-${updatedAnalysis.title}`}
      >
        <ErrorBoundary
          fallback={
            <Stack
              direction="row"
              alignItems="center"
              pl={2}
              py={2}
              spacing={1}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <WarningAmberOutlined
                sx={{ color: 'error.light' }}
                fontSize="small"
              />
              <Typography variant="body2" color="error.light">
                Oops! Something went wrong.
              </Typography>
            </Stack>
          }
        >
          <Box
            onMouseEnter={onHeaderMouseEnter}
            onMouseLeave={onHeaderMouseLeave}
          >
            <DashboardAnalysisHeader
              rowID={rowID}
              analysis={updatedAnalysis}
              shouldDisableInteraction={shouldDisableInteraction}
            />
          </Box>
        </ErrorBoundary>
        <CardContent
          sx={{
            height: 'calc(100% - 70px)',
            p: '0 !important',
            minWidth: '100%',
          }}
        >
          <ErrorBoundary fallback={<FetchErrorMessage />}>
            <DashboardAnalysisContent
              analysis={updatedAnalysis}
              shouldDisableInteraction={shouldDisableInteraction}
            />
          </ErrorBoundary>
        </CardContent>
      </Card>
    </DashboardAnalysisProvider>
  );
};
